import { getLanguage } from './device-info';
import hasUserProvidedEmail from './has-provided-email';
import { parseEscapedJSONString } from '../json/parse';
import { EcommerceItem, pushEcommerceEvent } from '../analytics/ga/ecommerce';
import triggerEvent, { GAEventName, SaveAction, SaveEvent } from '../analytics/ga/events';

function updateState( element, action, className ) {
    const replacable = element.querySelector( '.text' ) || element;

    if ( action === 'save' && !element.classList.contains( className ) ) {
        element.dataset.saveSave = replacable.innerHTML;
        replacable.innerHTML = element.dataset.saveSaved;
        element.classList.add( className );
    }

    if ( action === 'remove' && element.classList.contains( className ) ) {
        replacable.innerHTML = element.dataset.saveSave;
        element.classList.remove( className );
    }

    return function() {
        if ( action === 'save' && element.classList.contains( className ) ) {
            replacable.innerHTML = element.dataset.saveSave;
            element.classList.remove( className );
        }

        if ( action === 'remove' && !element.classList.contains( className ) ) {
            element.dataset.saveSave = replacable.innerHTML;
            replacable.innerHTML = element.dataset.saveSaved;
            element.classList.add( className );
        }
    };
}

function saveFromElement() {
    const save = async function( el: HTMLButtonElement, forceSave: boolean = false, data: any = {} ) {
        if ( forceSave && typeof window.savedSearch !== 'undefined' && window.savedSearch ) {
            return;
        }

        if ( el.disabled ) {
            return;
        }

        el.disabled = true;

        let action;
        let favClassName;

        if ( typeof window.savedSearch !== 'undefined' && !window.savedSearch && forceSave ) {
            action = 'save';
            favClassName = 'favourite';
        } else {
            favClassName = el.classList.contains( 'favourite-button' ) ? 'favourite' : 'favourite-alt';
            action = el.classList.contains( favClassName ) ? 'remove' : 'save';
        }

        const undoStateChange = updateState( el, action, favClassName );

        const formData = {
            property: '',
            value: '',
            meta: '',
            displayName: '',
        };

        if ( data.saveProperty && data.saveValue && data.saveMeta ) {
            formData.property = data.saveProperty;
            formData.value = data.saveValue;
            formData.meta = data.saveMeta;
        } else {
            formData.property = el.dataset.saveProperty || '';
            formData.value = el.dataset.saveValue || '';
            formData.meta = el.dataset.saveMeta || '';
        }

        const displayName = data.saveDisplayname;

        if ( formData.property === 'property-search' && displayName && displayName.length > 0 ) {
            formData.displayName = displayName;
        }

        let langPrefix = '';

        const lang = getLanguage();

        if ( lang !== 'en' ) {
            langPrefix = '/' + lang;
        }

        const isPropertySearchBeingSaved = action === 'save' && ( formData.property === 'property-search' );
        const isPropertyBeingSaved = action === 'save' && formData.property === 'property';
        const isPropertyBeingUnSaved = action !== 'save' && formData.property === 'property';

        if ( isPropertySearchBeingSaved ) {
            window.savedSearch = true;
        }

        if ( formData.property === 'property-search' && action === 'remove' ) {
            window.savedSearch = false;
        }

        const url = new URL( window.location.origin + langPrefix + '/saved/' + action );

        const elDescription = 'tag:' + el.tagName + ',id:' + el.id + ',class:' + el.className;
        // property & value are required
        // displayName & meta are not
        const form = new FormData();
        form.append( 'property', formData.property );
        form.append( 'value', formData.value );

        if ( formData.displayName ) {
            form.append( 'displayName', formData.displayName );
        }

        if ( formData.meta ) {
            form.append( 'meta', formData.meta );
        }

        form.append( 'el', elDescription );

        const response = await window.http( url, {
            method: 'POST',
            body: form,
        } );

        if ( !response.ok ) {
            undoStateChange();
            el.disabled = false;
            throw new Error( 'failed saving' );
        }

        window.dispatchEvent( new CustomEvent( 'twc:update-saved-counter', {
            bubbles: true,
            detail: {
                delta: action === 'save' ? 1 : -1,
            },
        } ) );

        const savables = document.getElementsByClassName( 'twc__savable' ) as HTMLCollectionOf<HTMLElement>;

        for ( let i = 0; i < savables.length; i++ ) {
            if ( !savables[ i ].dataset.saveProperty || savables[ i ].dataset.saveProperty !== formData.property ) {
                continue;
            }
            if ( savables[ i ].dataset.saveValue !== formData.value ) {
                continue;
            }

            updateState( savables[ i ], action, favClassName );
        }

        if ( ( isPropertySearchBeingSaved || isPropertyBeingSaved ) && !forceSave ) {
            hasUserProvidedEmail()
                .then( function( hasProvidedEmail ) {
                    if ( hasProvidedEmail ) {
                        return;
                    }

                    const eventDetail = {
                        id: '',
                    };

                    if ( isPropertyBeingSaved ) {
                        eventDetail.id = 'save-property';
                    }

                    if ( isPropertySearchBeingSaved ) {
                        eventDetail.id = 'save-search';
                    }

                    document.dispatchEvent( new CustomEvent( 'twc:popup:open-property-alert', {
                        bubbles: true,
                        detail: eventDetail,
                    } ) );
                } );
        }

        const hasAddToCartEvent = el.dataset.addToCartEvent && el.dataset.addToCartEvent.length > 0;
        const hasRemoveFromCartEvent = el.dataset.removeFromCartEvent && el.dataset.removeFromCartEvent.length > 0;

        const updateEventListValues = function( event, elementEventSelector, elementEventAttr ) {
            const matchingElementWithEvent = Array.from( document.querySelectorAll( elementEventSelector ) ).find( ( element ) => {
                if ( element.closest( '#page-popup-container' ) ) {
                    return false;
                }

                const parsed = parseEscapedJSONString( element.dataset[ elementEventAttr ] );

                return parsed.ecommerce.items[ 0 ].item_id === event.ecommerce.items[ 0 ].item_id;
            } );

            if ( !matchingElementWithEvent ) {
                return event;
            }

            const matchingEvent = parseEscapedJSONString( matchingElementWithEvent.dataset[ elementEventAttr ] );

            // eslint-disable-next-line camelcase
            event.ecommerce.items[ 0 ].item_list_id = matchingEvent.ecommerce.items[ 0 ].item_list_id;

            // eslint-disable-next-line camelcase
            event.ecommerce.items[ 0 ].item_list_name = matchingEvent.ecommerce.items[ 0 ].item_list_name;

            return event;
        };

        let ecommerceItem: EcommerceItem | null = null;

        if ( isPropertyBeingSaved && hasAddToCartEvent ) {
            let event = parseEscapedJSONString( el.dataset.addToCartEvent || '{}' );

            if ( event.ecommerce.items[ 0 ].item_list_id === 'no_list_available' ) {
                event = updateEventListValues( event, '[data-add-to-cart-event]', 'addToCartEvent' );
                el.dataset.addToCartEvent = JSON.stringify( event );
            }

            if ( event.ecommerce && event.ecommerce.items ) {
                ecommerceItem = event.ecommerce.items[ 0 ];
            }

            pushEcommerceEvent( event );
        }

        if ( isPropertyBeingUnSaved && hasRemoveFromCartEvent ) {
            let event = parseEscapedJSONString( el.dataset.removeFromCartEvent || '{}' );

            if ( event.ecommerce.items[ 0 ].item_list_id === 'no_list_available' ) {
                event = updateEventListValues( event, '[data-remove-from-cart-event]', 'removeFromCartEvent' );
                el.dataset.removeFromCartEvent = JSON.stringify( event );
            }

            if ( event.ecommerce && event.ecommerce.items ) {
                ecommerceItem = event.ecommerce.items[ 0 ];
            }

            pushEcommerceEvent( event );
        }

        if ( isPropertySearchBeingSaved && el.dataset.addToWishlistEvent && el.dataset.addToWishlistEvent.length > 0 ) {
            const event = parseEscapedJSONString( el.dataset.addToWishlistEvent );

            pushEcommerceEvent( parseEscapedJSONString( el.dataset.addToWishlistEvent ) );

            if ( event.ecommerce && event.ecommerce.items ) {
                ecommerceItem = event.ecommerce.items[ 0 ];
            }
        }

        let saveEventParameters: SaveEvent = {
            action: action === 'save' ? SaveAction.Save : SaveAction.Unsave,
        };

        if ( ecommerceItem ) {
            saveEventParameters = {
                ...saveEventParameters,
                'item_id': ecommerceItem.item_id,
                'item_name': ecommerceItem.item_name,
                'item_brand': ecommerceItem.item_brand,
                'item_variant': ecommerceItem.item_variant,
                'item_category': ecommerceItem.item_category,
                'item_category2': ecommerceItem.item_category2,
                'item_category3': ecommerceItem.item_category3,
                'item_category4': ecommerceItem.item_category4,
                'item_category5': ecommerceItem.item_category5,
            };
        }

        triggerEvent<SaveEvent>( GAEventName.ItemSave, saveEventParameters );

        const savedEvent: Record<string, any> = {
            saved: action === 'save',
            type: formData.property,
            meta: {},
            value: null,
        };

        if ( formData.meta && formData.meta.length > 0 ) {
            savedEvent.meta = JSON.parse( formData.meta );
        }

        if ( /-search/i.test( formData.property ) ) {
            savedEvent.value = JSON.parse( formData.value );
        } else {
            savedEvent.value = parseInt( formData.value );
        }

        document.dispatchEvent( new CustomEvent( 'twc:update-saved-status', {
            bubbles: true,
        } ) );

        document.dispatchEvent( new CustomEvent( 'twc:saved-change', {
            bubbles: true,
            detail: savedEvent,
        } ) );

        el.disabled = false;
    };

    window.addEventListener( 'twc:saveable', function( event: CustomEvent ) {
        if ( !event.detail ) {
            return;
        }
        save( event.detail.element, event.detail.forceSave, event.detail.data );
    } );

    document.addEventListener( 'click', function( event ) {
        if ( !event.target ) {
            return;
        }

        const target = event.target as HTMLButtonElement;

        if ( !( 'saveable' in target.dataset ) ) {
            return;
        }

        event.preventDefault();

        const requiresHandler = (
            target.dataset.requiresHandler &&
            target.dataset.requiresHandler === 'true'
        );

        if ( requiresHandler ) {
            event.target.dispatchEvent( new CustomEvent( 'twc-save:' + target.dataset.handler, {
                bubbles: true,
            } ) );
            return;
        }

        save( target, false );
    } );
}

export default saveFromElement;
